import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/hooks/useI18n';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Built-in copy for the error page. Translations are fetched over the network
 * after mount, so a crash before they arrive — or while offline, or after a
 * failed chunk load — would otherwise leave raw keys on screen. Mirrors the
 * entries in `public/locales/{lang}/common.json`.
 */
const FALLBACK_TEXT: Record<string, Record<string, string>> = {
  en: {
    'common.errorPageTitle': 'Something went wrong',
    'common.errorPageDescription':
      'Sorry, an unexpected error occurred. Please try reloading the page.',
    'common.reload': 'Reload',
  },
  zh: {
    'common.errorPageTitle': '页面出错了',
    'common.errorPageDescription': '抱歉，页面遇到了意外错误。请尝试重新加载页面。',
    'common.reload': '重新加载',
  },
  ms: {
    'common.errorPageTitle': 'Sesuatu telah berlaku',
    'common.errorPageDescription':
      'Maaf, ralat tidak dijangka berlaku. Sila cuba muat semula halaman.',
    'common.reload': 'Muat Semula',
  },
};

function ErrorFallback() {
  const { t: translate, lang } = useI18n();
  const fallback = FALLBACK_TEXT[lang] ?? FALLBACK_TEXT.en!;
  // t() echoes the key back when translations have not loaded
  const t = (key: string) => {
    const text = translate(key);
    return text === key ? (fallback[key] ?? key) : text;
  };
  return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-4 p-4">
      <div className="text-destructive mb-2">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      </div>
      <h1 className="text-2xl font-bold text-foreground">{t('common.errorPageTitle')}</h1>
      <p className="text-muted-foreground text-center max-w-md">
        {t('common.errorPageDescription')}
      </p>
      <Button onClick={() => window.location.reload()}>
        <RefreshCw className="h-4 w-4" />
        {t('common.reload')}
      </Button>
    </div>
  );
}

/**
 * React Error Boundary with Sentry error reporting and fallback UI.
 *
 * Catches rendering errors in the component tree, reports them to
 * Sentry (when configured), and displays a user-friendly error page
 * with a reload button.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Report to Sentry with component stack context
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });

    // Also log to console for local development
    console.error('[ErrorBoundary]', error, errorInfo.componentStack);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return <ErrorFallback />;
    }

    return this.props.children;
  }
}
